import { AudioPreset } from '../types';

export const DEFAULT_PRESETS: AudioPreset[] = [
  // --- MINDFULNESS & RESTORATIVE SOUNDSCAPES ---
  {
    name: 'Wind Chimes in Mist',
    category: 'Pluck',
    colorHex: '#a5f3fc',
    tuningSystem: '432Hz',
    ambientBed: 'forest-breeze',
    harmonicsType: 'chime',
    oscillatorType: 'sine',
    filterType: 'lowpass',
    baseFrequency: 432,
    scale: [60, 62, 64, 67, 69, 72, 74, 76], // Warm Mid-Register Pentatonic
    attack: 0.08,
    decay: 0.6,
    sustain: 0.2,
    release: 2.8,
    filterQ: 1.2,
    reverbMix: 0.75,
    delayTime: 0.35,
    pitchMod: 0,
    detuneSpread: 2,
    description: 'Delicate warm metallic wind chime droplets floating over gentle mountain mist.'
  },
  {
    name: 'Tibetan Sing Bowl (432Hz)',
    category: 'Healing',
    colorHex: '#38bdf8',
    tuningSystem: '432Hz',
    ambientBed: 'singing-bowl',
    harmonicsType: 'bell',
    oscillatorType: 'sine',
    filterType: 'lowpass',
    baseFrequency: 216,
    scale: [48, 52, 55, 60, 62, 64, 67, 72], // Grounded 432Hz Major Pentatonic
    attack: 0.9,
    decay: 1.4,
    sustain: 0.85,
    release: 3.6,
    filterQ: 1.0,
    reverbMix: 0.8,
    delayTime: 0.45,
    pitchMod: 0,
    detuneSpread: 3,
    description: 'Resonant Tibetan metal singing bowls with rich overtone partials tuned to 432 Hz.'
  },
  {
    name: 'Solfeggio 528Hz Miracle',
    category: 'Healing',
    colorHex: '#10b981',
    tuningSystem: '528Hz',
    ambientBed: 'binaural-theta',
    harmonicsType: 'crystal',
    oscillatorType: 'sine',
    filterType: 'lowpass',
    baseFrequency: 264,
    scale: [48, 52, 55, 59, 62, 64, 67, 71], // Warm Solfeggio Harmonic
    attack: 1.0,
    decay: 1.5,
    sustain: 0.9,
    release: 3.8,
    filterQ: 0.9,
    reverbMix: 0.82,
    delayTime: 0.5,
    pitchMod: 0,
    detuneSpread: 4,
    description: 'The ancient 528 Hz transformation frequency layered over gentle 6 Hz theta binaural pulse.'
  },
  {
    name: 'Ethereal Cloud Swell',
    category: 'Mindfulness',
    colorHex: '#818cf8',
    tuningSystem: '432Hz',
    ambientBed: 'warm-drone',
    harmonicsType: 'warm-analog',
    oscillatorType: 'triangle',
    filterType: 'lowpass',
    baseFrequency: 216,
    scale: [48, 53, 55, 60, 62, 65, 67, 72], // Soft Lydian Floating Scale
    attack: 1.2,
    decay: 0.9,
    sustain: 0.85,
    release: 3.2,
    filterQ: 0.8,
    reverbMix: 0.8,
    delayTime: 0.4,
    pitchMod: 0,
    detuneSpread: 3,
    description: 'Soft, slow-blooming celestial pads that expand with slow arm gestures and deep breathing.'
  },
  {
    name: 'Zen Bamboo Garden',
    category: 'Acoustic',
    colorHex: '#34d399',
    tuningSystem: '432Hz',
    ambientBed: 'forest-breeze',
    harmonicsType: 'bamboo',
    oscillatorType: 'sine',
    filterType: 'lowpass',
    baseFrequency: 432,
    scale: [48, 51, 55, 58, 60, 63, 67, 70], // Japanese Insen/Yo Grounded
    attack: 0.06,
    decay: 0.8,
    sustain: 0.25,
    release: 2.2,
    filterQ: 1.1,
    reverbMix: 0.65,
    delayTime: 0.35,
    pitchMod: 0,
    detuneSpread: 2,
    description: 'Organic bamboo chime and wooden koto plucks with subtle forest wind ambience.'
  },
  {
    name: 'Crystal Chakra Drone',
    category: 'Chakra',
    colorHex: '#c084fc',
    tuningSystem: '432Hz',
    ambientBed: 'singing-bowl',
    harmonicsType: 'crystal',
    oscillatorType: 'sine',
    filterType: 'lowpass',
    baseFrequency: 136.1, // Earth Om frequency
    scale: [36, 43, 48, 55, 60, 64, 67, 72], // Low Open Harmonic Series
    attack: 1.5,
    decay: 1.6,
    sustain: 0.95,
    release: 4.5,
    filterQ: 0.9,
    reverbMix: 0.85,
    delayTime: 0.6,
    pitchMod: 0,
    detuneSpread: 4,
    description: 'Grounding 136.1 Hz Earth OM frequency with pure quartz crystal singing bowl resonance.'
  },
  {
    name: 'Ocean Calm & Theta Waves',
    category: 'Mindfulness',
    colorHex: '#06b6d4',
    tuningSystem: '432Hz',
    ambientBed: 'ocean',
    harmonicsType: 'bell',
    oscillatorType: 'sine',
    filterType: 'lowpass',
    baseFrequency: 216,
    scale: [43, 47, 50, 55, 59, 62, 67, 71], // G Major 9 Serenity Grounded
    attack: 1.3,
    decay: 1.2,
    sustain: 0.85,
    release: 3.5,
    filterQ: 0.8,
    reverbMix: 0.82,
    delayTime: 0.5,
    pitchMod: 0,
    detuneSpread: 4,
    description: 'Continuous gentle ocean shore wash combined with soft bell blooms for nervous system regulation.'
  },
  {
    name: 'Deep Delta Rest',
    category: 'Healing',
    colorHex: '#6366f1',
    tuningSystem: '432Hz',
    ambientBed: 'binaural-theta',
    harmonicsType: 'warm-analog',
    oscillatorType: 'triangle',
    filterType: 'lowpass',
    baseFrequency: 65.4,
    scale: [36, 43, 48, 55, 60, 63, 67], // C Minor Deep Grounding
    attack: 2.0,
    decay: 1.8,
    sustain: 0.95,
    release: 4.8,
    filterQ: 0.7,
    reverbMix: 0.88,
    delayTime: 0.65,
    pitchMod: 0,
    detuneSpread: 4,
    description: 'Warm sub-bass foundation and delta brainwave frequencies for sleep and deep somatic calm.'
  },
  {
    name: 'Warm Lofi Rhodes',
    category: 'Ambient',
    colorHex: '#e879f9',
    tuningSystem: '440Hz',
    ambientBed: 'warm-drone',
    harmonicsType: 'warm-analog',
    oscillatorType: 'sine',
    filterType: 'lowpass',
    baseFrequency: 220,
    scale: [45, 48, 52, 55, 59, 60, 64], // A minor 9 Low
    attack: 0.15,
    decay: 0.6,
    sustain: 0.7,
    release: 2.0,
    filterQ: 0.8,
    reverbMix: 0.55,
    delayTime: 0.35,
    pitchMod: 0,
    detuneSpread: 3,
    description: 'Mellow, nostalgic warm electric piano tones for focused mindfulness and study.'
  },
  {
    name: 'Vintage Warm Brass',
    category: 'Retro',
    colorHex: '#f59e0b',
    tuningSystem: '440Hz',
    harmonicsType: 'warm-analog',
    oscillatorType: 'triangle',
    filterType: 'lowpass',
    baseFrequency: 146.83,
    scale: [38, 42, 45, 47, 50, 54, 57], // D Major Pentatonic Low
    attack: 0.4,
    decay: 0.9,
    sustain: 0.75,
    release: 2.2,
    filterQ: 1.0,
    reverbMix: 0.6,
    delayTime: 0.4,
    pitchMod: 0,
    detuneSpread: 3,
    description: 'Warm, mellow analog synthesizer brass and harmonically rich chords without harsh bite.'
  },
  {
    name: 'Soft Earth Pulse',
    category: 'Electronic',
    colorHex: '#ec4899',
    tuningSystem: '432Hz',
    harmonicsType: 'warm-analog',
    oscillatorType: 'sine',
    filterType: 'lowpass',
    baseFrequency: 108,
    scale: [36, 40, 43, 48, 52, 55, 60], // Grounded Minor Pentatonic
    attack: 0.12,
    decay: 0.5,
    sustain: 0.6,
    release: 1.2,
    filterQ: 1.0,
    reverbMix: 0.45,
    delayTime: 0.25,
    pitchMod: 0,
    description: 'Gentle, soothing sub-pulse for somatic movement and rhythmic centering.'
  },
  {
    name: 'Solar Winds',
    category: 'Space',
    colorHex: '#fb923c',
    tuningSystem: '432Hz',
    ambientBed: 'forest-breeze',
    harmonicsType: 'pure',
    oscillatorType: 'noise',
    filterType: 'lowpass',
    baseFrequency: 220,
    scale: [48, 55, 60, 67, 72],
    attack: 1.0,
    decay: 1.2,
    sustain: 0.85,
    release: 3.2,
    filterQ: 1.2,
    reverbMix: 0.8,
    delayTime: 0.5,
    pitchMod: 0,
    description: 'Soft, atmospheric mountain wind sweeps with gentle harmonic air warmth.'
  }
];
